import { createForm } from '@seek/forms-ui';
import { required } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  Button,
  PasswordField,
  Stack,
  Text,
} from 'braid-design-system';
import React from 'react';

import { login } from '../../../../modules/Auth0/login';
import { Flow, type FriendlyError } from '../../../../modules/Auth0/types';
import {
  mappings,
  recaptchaNonResetErrors,
} from '../../../../modules/Auth0/utils/mapError';
import { useAnalyticsContext } from '../../../../modules/analytics/AnalyticsContext';
import type { FormProps } from '../../../../types';
import getHashRoute from '../../../../utils/getHashRoute';
import { handleFormSubmit } from '../../../../utils/handleFormSubmit';
import isTestEmail from '../../../../utils/isTestEmail';
import { useAppContext } from '../../../AppContext';
import useTrackError from '../../../hooks/analytics/useTrackError';
import useTrackPageView from '../../../hooks/analytics/useTrackPageView';
import useFormLoadMetrics from '../../../hooks/metrics/useFormLoadMetrics';
import { useBotState } from '../../../hooks/useBotState';
import useFormState from '../../../hooks/useFormState';
import FormCard from '../../../shared/FormCard/FormCard';
import FormHeader from '../../../shared/FormHeader/FormHeader';
import FormStack from '../../../shared/FormStack';
import Nudge from '../../../shared/Nudge/Nudge';
import ReCaptcha from '../../../shared/ReCaptcha/ReCaptcha';
import getCaptchaToken from '../../../shared/ReCaptcha/getCaptchaToken';
import {
  escapeSubmit,
  requireCloudflareBotCheck,
} from '../../../shared/ReCaptcha/utils';
import ServerError from '../../../shared/ServerError/ServerError';
import SocialButtons from '../../../shared/SocialButtons/SocialButtons';
import TertiaryLinks from '../../../shared/TertiaryLinks';
import TrackLink from '../../../shared/TrackLink/TrackLink';
import { useLoginPageContext } from '../LoginPageContext';
import { composeUrl } from '../composeExpiredPasswordUrl';
import { FORGOT_PASSWORD } from '../hashRoutes';
import EmailField from '../shared/components/EmailField/EmailField';
import SingleSignOnButton from '../shared/components/SingleSignOnButton/SingleSignOnButton';

import translations from './.vocab';
import LoginMessage from './LoginMessage/LoginMessage';
import { escapedLoginFormMessages } from './LoginMessage/types';

import * as styles from '../LoginPageStyles.css';

interface FormValues {
  emailAddress: string;
  password: string;
}

const { FormProvider, useField } = createForm<FormValues>();

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface Props extends FormProps<FormValues> {}

const LoginForm = ({ handleSubmit }: Props) => {
  const { t } = useTranslations(translations);
  const {
    loginMessage,
    setFlow,
    realm: realmOverride,
    emailAddress,
  } = useLoginPageContext();

  setFlow(Flow.Login);
  useFormLoadMetrics('login');
  const [formState, setFormState] = useFormState<FriendlyError>();
  const {
    realm,
    registerHref,
    isMobileClient,
    tenant,
    site,
    googleCaptchaSiteKey,
  } = useAppContext();
  const isBotCheckRequired = requireCloudflareBotCheck(site);

  const password = useField({
    id: 'password',
    validators: [required],
  });

  useTrackPageView('login_form_displayed', 'login form', 'login');

  const { loading, submitted, error } = formState;
  useTrackError({ error });

  const { analytics } = useAnalyticsContext();
  const [botState, setBotState] = useBotState();
  const { isBotCheckDone, isRenderRecaptchaStarted, isRenderRecaptchaDone } =
    botState;

  const onSubmit = handleSubmit(async (formValues) => {
    if (
      escapeSubmit({
        isBotCheckRequired,
        isBotCheckDone,
        isRenderRecaptchaStarted,
        isRenderRecaptchaDone,
      })
    )
      return;

    let captchaToken;
    if (isRenderRecaptchaDone && !isTestEmail(site, formValues.emailAddress)) {
      captchaToken = await getCaptchaToken(Flow.Login, googleCaptchaSiteKey!);
      if (!captchaToken) {
        setFormState({
          error: recaptchaNonResetErrors.recaptcha_required,
        });
        return;
      }
    }
    handleFormSubmit(
      login(analytics, {
        ...formValues,
        realm: realmOverride || realm,
        isMobileClient,
        captchaToken,
      }),
      setFormState,
      'login',
    );
  });

  const showRecaptcha = !isTestEmail(site, emailAddress);

  const pageContentTranslated = {
    password: t('Password'),
    signIn: t('Sign in'),
    forgotPassword: t('Forgot password?'),
    successSignedIn: t(
      'You have successfully signed in. You will be redirected shortly.',
    ),
    tertiaryRegisterLink: t('Don’t have an account? Register', {
      RegisterLink: (v) => (
        <TrackLink
          key="link"
          href={registerHref}
          trackingData={{
            eventName: 'register_pressed',
            linkPosition: 'body',
            isThirdPartyLink: 'false',
            registrationFlow: 'new user',
          }}
        >
          {v}
        </TrackLink>
      ),
    }),
  };

  const shouldRedirectToExpiredPassword =
    error &&
    error === mappings.password_change_required &&
    tenant === 'seekanz';

  if (shouldRedirectToExpiredPassword) {
    window.location.assign(composeUrl(emailAddress));
    return <div />;
  }

  const shouldShowLoginMessage =
    !submitted &&
    loginMessage &&
    !escapedLoginFormMessages.includes(loginMessage);

  return (
    <Stack space="none">
      {isBotCheckRequired && (
        <ReCaptcha
          formError={error}
          formLoading={loading}
          formSubmitted={submitted}
          setFormState={setFormState}
          isBotCheckDone={isBotCheckDone}
          isRenderRecaptchaStarted={isRenderRecaptchaStarted}
          isRenderRecaptchaDone={isRenderRecaptchaDone}
          setBotState={setBotState}
          flow={Flow.Login}
          showRecaptcha={showRecaptcha}
        />
      )}
      <Nudge />
      <FormCard>
        {shouldShowLoginMessage && <LoginMessage message={loginMessage} />}
        <FormHeader title="Sign in" />
        {submitted ? (
          <Alert tone="positive">
            <Text>{pageContentTranslated.successSignedIn}</Text>
          </Alert>
        ) : (
          <form id={`signin_${tenant}`} onSubmit={onSubmit} noValidate>
            <FormStack>
              <EmailField useField={useField} formName="login" />
              <PasswordField
                id="password"
                name={`password_${tenant}`}
                autoComplete={`${tenant} current-password`}
                label={pageContentTranslated.password}
                message={(() => {
                  if (!password.valid) {
                    return <span>{password.errorMessage}</span>;
                  }
                })()}
                tone={Boolean(password.valid) ? 'neutral' : 'critical'}
                value={password.value}
                onChange={password.onChange}
                onBlur={password.onBlur}
                reserveMessageSpace
                tertiaryLabel={
                  <TrackLink
                    id="forgot-password"
                    href={getHashRoute(FORGOT_PASSWORD)}
                    trackingData={{
                      eventName: 'forgot_password_pressed',
                      linkPosition: 'body',
                      isThirdPartyLink: 'false',
                    }}
                  >
                    {pageContentTranslated.forgotPassword}
                  </TrackLink>
                }
                visibilityToggleLabel={t('Toggle password visibility')}
              />

              {error && (
                <ServerError error={error} emailAddress={emailAddress} />
              )}

              <Stack space="none">
                <Box width="full" display="flex" justifyContent="center">
                  <div
                    id="recaptchaCheckbox"
                    className={
                      showRecaptcha ? styles.recaptcha : styles.hiddenRecaptcha
                    }
                  />
                </Box>
                <Button
                  data={{ cy: 'login' }}
                  type="submit"
                  tone="brandAccent"
                  loading={loading}
                >
                  {pageContentTranslated.signIn}
                </Button>
                <SingleSignOnButton />
              </Stack>
            </FormStack>
            <SocialButtons form="login" />
          </form>
        )}
        <TertiaryLinks>
          <Text>{pageContentTranslated.tertiaryRegisterLink}</Text>
        </TertiaryLinks>
      </FormCard>
    </Stack>
  );
};

export default () => {
  const { emailAddress } = useLoginPageContext();
  const { language } = useAppContext();
  return (
    <FormProvider
      initialValues={{ emailAddress, password: '' }}
      language={language}
    >
      {({ handleSubmit }) => <LoginForm handleSubmit={handleSubmit} />}
    </FormProvider>
  );
};
